/**
 * RecipeCostSummary Component
 *
 * Displays cost breakdown for a recipe, showing material cost per batch
 * and per block, with stock availability warnings.
 *
 * User Story: US-P6-002 - Recipe Cost Integration
 * Step 8: Recipe Builder UI Enhancement
 */

import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Loader2, PoundSterling } from 'lucide-react'

/**
 * Format a number as GBP currency
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted currency string
 */
function formatCurrency(amount) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '—'
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

/**
 * RecipeCostSummary - Displays recipe cost totals and warnings
 *
 * @param {Object} props
 * @param {Object} props.recipeCost - Cost breakdown from useRecipeCost
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.error - Error message if any
 * @param {string} props.className - Additional CSS classes
 */
export default function RecipeCostSummary({
  recipeCost,
  isLoading = false,
  error = null,
  className = ''
}) {
  // Loading state
  if (isLoading) {
    return (
      <div className={`p-4 bg-surface-secondary rounded-lg ${className}`}>
        <div className="flex items-center gap-2 text-content-secondary">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Calculating costs...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className={className}>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Error calculating cost: {error}
        </AlertDescription>
      </Alert>
    )
  }

  // No cost data yet
  if (!recipeCost) {
    return (
      <div className={`p-4 bg-surface-secondary rounded-lg ${className}`}>
        <div className="text-content-secondary text-sm">
          Add ingredients to see cost estimate
        </div>
      </div>
    )
  }

  // Get ingredients with insufficient stock
  const insufficientStockIngredients = recipeCost.ingredients?.filter(
    ing => ing.track_inventory && !ing.sufficient_stock
  ) || []

  const hasStockIssues = insufficientStockIngredients.length > 0

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Cost Summary Card */}
      <div className="p-4 bg-surface-secondary rounded-lg">
        <h4 className="text-sm font-medium text-content-secondary mb-3 flex items-center gap-2">
          <PoundSterling className="h-4 w-4" />
          Cost Summary
        </h4>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-content-tertiary">
              Material Cost per Batch
            </p>
            <p className="text-lg font-semibold text-content-primary">
              {formatCurrency(recipeCost.total_cost_per_batch)}
            </p>
          </div>
          <div>
            <p className="text-xs text-content-tertiary">
              Material Cost per Block
            </p>
            <p className="text-lg font-semibold text-content-primary">
              {formatCurrency(recipeCost.total_cost_per_block)}
            </p>
          </div>
        </div>

        {recipeCost.blocks_per_batch && (
          <p className="text-xs text-content-tertiary mt-2">
            Based on {recipeCost.blocks_per_batch} blocks per batch
          </p>
        )}
      </div>

      {/* Stock Warning */}
      {hasStockIssues && (
        <Alert variant="warning" className="border-odin-amber bg-odin-amber/10">
          <AlertTriangle className="h-4 w-4 text-odin-amber" />
          <AlertDescription className="text-sm">
            <strong>Insufficient stock for:</strong>{' '}
            {insufficientStockIngredients
              .map(ing => ing.item_name || ing.material_name)
              .join(', ')}
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}
